import Recorder from 'recorder-core';
import 'recorder-core/src/engine/mp3';
import 'recorder-core/src/engine/mp3-engine';
import 'recorder-core/src/extensions/waveview';

class AudioRecorder {
    constructor() {
        this.rec = null; // 录音核心实例
        this.processTime = null; // 最后一次 onProcess 时间戳
        this.watchDogTimer = null; // 看门狗定时器
        this.wave = null; // 波形可视化对象
        this.startTime = null; // 开始录音时间
        this.blob = null; // 存储录音结果 blob
        this.duration = 0; // 存储录音时长
        this.isRecording = false; // 是否正在录音
        this.isPaused = false; // 是否已暂停
    }

    /**
     * 初始化录音器
     */
    initRecorder() {
        this.rec = Recorder({
            type: "mp3",
            sampleRate: 16000,
            bitRate: 16,
            onProcess: (buffers, powerLevel, bufferDuration, bufferSampleRate, newBufferIdx, asyncEnd) => {
                this.processTime = Date.now();
                if (this.wave) {
                    this.wave.input(buffers[buffers.length - 1], powerLevel, bufferSampleRate);
                }
            }
        });
    }

    /**
     * 打开麦克风并请求权限
     * @param {Function} success 成功回调
     */
    open(success) {
        this.initRecorder();

        this.rec.open(() => {
            if (Recorder.WaveView) {
                this.wave = Recorder.WaveView({ elem: ".recwave" });
            }
            success && success();
        }, (msg, isUserNotAllow) => {
            console.error((isUserNotAllow ? "UserNotAllow，" : "") + "无法录音:" + msg);
        });
    }

    /**
     * 开始录音
     */
    start() {
        if (!this.rec) {
            console.error("录音器未初始化");
            return;
        }

        if (this.isRecording) {
            console.warn("已经在录音中");
            return;
        }

        this.rec.start();

        this.isRecording = true;
        this.isPaused = false;

        // 启动看门狗定时器，监控录音状态
        this.watchDogTimer = setInterval(() => {
            if (!this.isRecording || Date.now() < this.rec.wdtPauseT) return;

            if (Date.now() - (this.processTime || this.startTime) > 1500) {
                clearInterval(this.watchDogTimer);
                console.error("录音中断或失败");
                this.stop();
            }
        }, 1000);

        this.startTime = Date.now();
        this.rec.wdtPauseT = 0;
        this.processTime = 0;
    }

    /**
     * 停止录音
     * @returns {Promise<void>}
     */
    stopAsync() {
        if (!this.isRecording) {
            console.warn("没有正在进行的录音");
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            this.rec.watchDogTimer = 0;
            this.rec.stop((blob, duration) => {
                this.blob = blob;
                this.duration = duration;

                // 关闭录音器但不销毁 rec 实例，便于复用
                this.rec.close();

                // 更新状态
                this.isRecording = false;
                this.isPaused = false;
                clearInterval(this.watchDogTimer);

                resolve();
            }, (msg) => {
                console.error("录音失败:", msg);
                this.isRecording = false;
                this.isPaused = false;
                clearInterval(this.watchDogTimer);
                reject(new Error(msg));
            });
        });
    }

    /**
     * 获取录音文件的本地 URL
     * @returns {string|null}
     */
    getRecordingURL() {
        if (!this.blob) {
            console.warn("没有可用的录音文件");
            return null;
        }
        return (window.URL || webkitURL).createObjectURL(this.blob);
    }

    /**
     * 播放录音（如果已停止）
     */
    playLastRecording() {
        const url = this.getRecordingURL();
        if (!url) return;

        const audio = document.createElement('audio');
        audio.controls = true;
        audio.src = url;
        document.body.appendChild(audio);
        audio.play();
    }

    /**
     * 暂停录音
     */
    pause() {
        if (!this.isRecording || this.isPaused) return;

        this.rec.pause();
        this.isPaused = true;
        this.rec.wdtPauseT = Date.now() * 2; // 暂停时不监控
    }

    /**
     * 恢复录音
     */
    resume() {
        if (!this.isRecording || !this.isPaused) return;

        this.rec.resume();
        this.isPaused = false;
        this.rec.wdtPauseT = Date.now() + 1000;
    }

    /**
     * 销毁录音器（完全释放资源）
     */
    destroy() {
        if (this.rec) {
            this.rec.close();
            this.rec = null;
        }

        if (this.watchDogTimer) {
            clearInterval(this.watchDogTimer);
            this.watchDogTimer = null;
        }

        if (this.wave) {
            this.wave = null;
        }

        if (this.blob) {
            this.blob = null;
        }

        this.processTime = null;
        this.startTime = null;
        this.duration = 0;
        this.isRecording = false;
        this.isPaused = false;
    }
}

export default new AudioRecorder();
